import { useState } from "react";
import { ChevronDownIcon, ClockIcon } from "@heroicons/react/24/outline";

import { clsxMerge, safeGetDate } from "shared/lib/helpers";
import {
  MeetingStatusChangeI,
  MeetingStatusEventTypeI,
} from "shared/lib/interfaces/account";
import { HistoryCardInfoItem } from "shared/ui/activity-log/cards-v2/base";

const EVENT_LABELS: Record<MeetingStatusEventTypeI, string> = {
  canceled: "Canceled",
  rescheduled: "Rescheduled",
  marked_show: "Marked as show",
  marked_no_show: "Marked as no-show",
  disputed: "Dispute opened",
  dispute_edited: "Dispute edited",
  dispute_resolved: "Dispute resolved",
};

/**
 * Qualifier shown after the actor's name.
 *
 * `ae_survey_link` is the one that matters: that endpoint is unauthenticated,
 * so the record says which AE the survey was emailed to, not who was signed in.
 * Presenting that as a verified action would be the same kind of false
 * confidence this panel exists to remove.
 */
const CHANNEL_QUALIFIERS: Partial<
  Record<MeetingStatusChangeI["channel"], string>
> = {
  ae_survey_link: "via emailed survey link",
  // Calendly and Calendar sync already read as systems; a partner's name does
  // not, so that one says outright that no person was involved.
  partner_webhook: "automatic",
};

const StatusChangeRow = ({ change }: { change: MeetingStatusChangeI }) => {
  const qualifier = change.is_inferred
    ? "recorded retroactively"
    : CHANNEL_QUALIFIERS[change.channel];

  return (
    <div className="flex flex-col gap-0.5">
      <h5 className="brand-typography-h5-bold @xl:brand-typography-body3">
        {EVENT_LABELS[change.event_type] ?? change.event_type}
      </h5>

      <p
        className={clsxMerge(
          "brand-typography-detail2",
          change.is_inferred && "italic"
        )}
      >
        {change.actor_name}
        {!!qualifier && ` (${qualifier})`}
        {!!change.created_at &&
          ` · ${safeGetDate(change.created_at, "MMM D, YYYY [at] h:mm A")}`}
      </p>
    </div>
  );
};

/**
 * The post-book audit trail for a meeting: who canceled, rescheduled, marked
 * it show / no-show, or disputed it.
 *
 * Only the most recent change is shown by default. A meeting can accumulate a
 * long trail (mark, dispute, edit, resolve, reschedule...) and the card has to
 * stay readable next to the Caller and AE lines, so the rest is behind a
 * toggle. The newest entry is the one that explains the meeting's current
 * status, which is what people come here for.
 *
 * Renders nothing when there is nothing recorded. Meetings that predate
 * attribution, and whose actor could not be inferred, simply have no trail —
 * which is more honest than naming someone we are guessing at.
 */
export const HistoryCardStatusChanges = ({
  statusChanges,
  className,
}: {
  statusChanges?: MeetingStatusChangeI[];
  className?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!statusChanges?.length) {
    return null;
  }

  // Newest first: the current status is what people look for.
  const [latestChange, ...earlierChanges] = [...statusChanges].reverse();
  const hasEarlierChanges = earlierChanges.length > 0;

  return (
    <HistoryCardInfoItem
      icon={<ClockIcon className="h-6 w-6" />}
      className={className}
    >
      <StatusChangeRow change={latestChange} />

      {isExpanded &&
        earlierChanges.map((change) => (
          <StatusChangeRow key={change.id} change={change} />
        ))}

      {hasEarlierChanges && (
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
          className="ae-link brand-typography-detail2 flex w-max items-center gap-1"
        >
          <ChevronDownIcon
            className={clsxMerge(
              "h-3 w-3 transition-transform",
              isExpanded && "rotate-180"
            )}
          />

          {isExpanded
            ? "Show less"
            : `${earlierChanges.length} earlier ${
                earlierChanges.length === 1 ? "change" : "changes"
              }`}
        </button>
      )}

      <p className="brand-typography-detail2 @xl:block hidden">
        Status history
      </p>
    </HistoryCardInfoItem>
  );
};
